import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import { appToday } from '../datetime'
import type { AppSettings, Category, Client, Entry, Todo } from '../../shared/types'
import { formatHours, formatHours2, parseDuration } from '../../shared/duration'
import { formatCents } from '../../shared/money'
import { pushError, pushToast } from '../toast'
import { useAutosave } from '../useAutosave'
import { useColumns, ColGroup, ColResizer, ColumnsMenu, WrapText, type ColumnDef } from '../columns'
import CategoryCombo from './CategoryCombo'
import ClientCombo from './ClientCombo'

const PX_PER_MIN = 1 // 60px per hour

function today(): string {
  return appToday()
}
function shiftDay(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
function hhmmToMin(v: string): number {
  const [h, m] = v.split(':').map(Number)
  return h * 60 + (m || 0)
}
function minToHHMM(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
function minToLabel(min: number): string {
  const h24 = Math.floor(min / 60)
  const m = min % 60
  const ampm = h24 < 12 ? 'AM' : 'PM'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}
function withAlpha(hex: string, alpha: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex + alpha : hex
}

export default function Today() {
  const [day, setDay] = useState(today())
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [allCats, setAllCats] = useState<Category[]>([])
  const [todos, setTodos] = useState<Todo[]>([])
  const [ready, setReady] = useState(false)
  // Calendar is the default; the list is the fast, keyboard-driven way to enter
  // several entries at once. Mirrors the To-Do screen's list/calendar switch.
  const [view, setView] = useState<'calendar' | 'list'>('calendar')

  useEffect(() => {
    ;(async () => {
      try {
        const [st, cl, cats, td] = await Promise.all([
          api.getSettings(),
          api.listClients(false),
          api.listAllCategories(),
          api.listTodos(),
        ])
        setSettings(st)
        setClients(cl)
        setAllCats(cats)
        setTodos(td)
      } catch (e) {
        pushError(`Couldn't load calendar: ${(e as Error).message}`)
      } finally {
        setReady(true)
      }
    })()
  }, [])

  if (!ready || !settings) return <div className="p-10 text-slate-400">Loading…</div>

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Today</h1>
          <div className="flex items-center gap-1">
            <button className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100" onClick={() => setDay(shiftDay(day, -1))} aria-label="Previous day">
              ‹
            </button>
            <input
              type="date"
              className="tabular rounded border border-slate-300 bg-white px-2 py-1 text-sm font-medium text-slate-800"
              value={day}
              onChange={(e) => setDay(e.target.value || today())}
            />
            <button className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100" onClick={() => setDay(shiftDay(day, 1))} aria-label="Next day">
              ›
            </button>
            <button className="ml-1 text-sm text-[var(--accent)] hover:underline" onClick={() => setDay(today())}>
              Today
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">{dayLabel(day)}</span>
          <div className="flex overflow-hidden rounded-md border border-slate-300 text-sm">
            {(['calendar', 'list'] as const).map((v) => (
              <button
                key={v}
                className={'px-3 py-1.5 capitalize ' + (view === v ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100')}
                onClick={() => setView(v)}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </header>

      {view === 'calendar' ? (
        <DayCalendar key={day} day={day} settings={settings} clients={clients} allCats={allCats} todos={todos} />
      ) : (
        <DayList key={day} day={day} clients={clients} allCats={allCats} todos={todos} />
      )}
    </div>
  )
}

// A saved entry always has a client; an unsaved list row may not have one yet.
type EntryRow = Omit<Entry, 'client_id'> & { client_id: number | null }

interface Block {
  entry: Entry
  startMin: number
  endMin: number
  col: number
  cols: number
  placed: boolean // has a real start_time (vs. defaulted to day-start)
}

type Selection =
  | { kind: 'draft'; startMin: number; endMin: number; client_id: number | null; description: string; category_id: number | null }
  | { kind: 'entry'; id: number }
  | null

// Entries with times are placed at those times; entries with only a duration
// (logged from the dashboard list or a to-do) default to the day's start time.
function layoutBlocks(entries: Entry[], defaultStart: number): Block[] {
  const items: Block[] = entries
    .filter((e) => e.start_time || e.duration_minutes > 0)
    .map((e) => {
      const placed = !!e.start_time
      const s = placed ? hhmmToMin(e.start_time!) : defaultStart
      const dur = placed && e.end_time ? Math.max(hhmmToMin(e.end_time) - hhmmToMin(e.start_time!), 5) : Math.max(e.duration_minutes, 5)
      return { entry: e, startMin: s, endMin: s + dur, col: 0, cols: 1, placed }
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)

  const out: Block[] = []
  let cluster: Block[] = []
  let clusterEnd = -1
  const flush = () => {
    if (!cluster.length) return
    const colEnds: number[] = []
    for (const it of cluster) {
      let placed = -1
      for (let i = 0; i < colEnds.length; i++) {
        if (it.startMin >= colEnds[i]) {
          placed = i
          break
        }
      }
      if (placed === -1) {
        placed = colEnds.length
        colEnds.push(it.endMin)
      } else colEnds[placed] = it.endMin
      it.col = placed
    }
    const cols = colEnds.length
    for (const it of cluster) out.push({ ...it, cols })
    cluster = []
    clusterEnd = -1
  }
  for (const it of items) {
    if (cluster.length && it.startMin >= clusterEnd) flush()
    cluster.push(it)
    clusterEnd = Math.max(clusterEnd, it.endMin)
  }
  flush()
  return out
}

function DayCalendar({
  day,
  settings,
  clients,
  allCats,
  todos,
}: {
  day: string
  settings: AppSettings
  clients: Client[]
  allCats: Category[]
  todos: Todo[]
}) {
  const interval = settings.daily_interval_minutes ?? 15
  const defaultStart = hhmmToMin(settings.daily_start_time ?? '08:00')
  const [viewStart, setViewStart] = useState(() => hhmmToMin(settings.daily_start_time ?? '08:00'))
  const [viewEnd, setViewEnd] = useState(() => hhmmToMin(settings.daily_end_time ?? '16:00'))
  const [entries, setEntries] = useState<Entry[]>([])
  const [sel, setSel] = useState<Selection>(null)
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null)
  const [moveDrag, setMoveDrag] = useState<{ id: number; startMin: number; dur: number } | null>(null)

  const colRef = useRef<HTMLDivElement>(null)
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  useEffect(() => {
    ;(async () => {
      try {
        const es = await api.listEntriesByDate(day)
        setEntries(es)
      } catch (e) {
        pushError(`Couldn't load day: ${(e as Error).message}`)
      }
    })()
  }, [day])

  const snap = useCallback((min: number) => Math.round(min / interval) * interval, [interval])
  const yToMin = useCallback(
    (clientY: number) => {
      const rect = colRef.current!.getBoundingClientRect()
      return viewStart + (clientY - rect.top) / PX_PER_MIN
    },
    [viewStart],
  )

  // drag-to-create
  function onColMouseDown(e: React.MouseEvent) {
    if (e.button !== 0) return
    const startMin = snap(yToMin(e.clientY))
    setDrag({ a: startMin, b: startMin + interval })
    const move = (ev: MouseEvent) => setDrag({ a: startMin, b: snap(yToMin(ev.clientY)) })
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      let s = startMin
      let en = snap(yToMin(ev.clientY))
      if (en < s) [s, en] = [en, s]
      if (en - s < interval) en = s + interval
      s = Math.max(0, s)
      en = Math.min(1440, en)
      setDrag(null)
      setSel({ kind: 'draft', startMin: s, endMin: en, client_id: null, description: '', category_id: null })
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  async function createFromDraft(d: Extract<Selection, { kind: 'draft' }>, clientId: number) {
    try {
      const created = await api.createEntry({
        client_id: clientId,
        entry_date: day,
        description: d.description,
        category_id: d.category_id,
        duration_minutes: d.endMin - d.startMin,
        start_time: minToHHMM(d.startMin),
        end_time: minToHHMM(d.endMin),
      })
      setEntries((es) => [...es, created])
      setSel({ kind: 'entry', id: created.id })
    } catch (e) {
      pushError(`Could not create block: ${(e as Error).message}`)
    }
  }

  async function patchEntry(id: number, patch: Partial<Entry>) {
    setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)))
    try {
      await api.updateEntry(id, patch)
    } catch (e) {
      pushError(`Save failed: ${(e as Error).message}`)
    }
  }

  async function deleteEntry(id: number) {
    const snapshot = entries.find((e) => e.id === id)
    setEntries((es) => es.filter((e) => e.id !== id))
    setSel(null)
    try {
      await api.deleteEntry(id)
      if (snapshot) {
        pushToast('Block deleted', {
          actionLabel: 'Undo',
          onAction: async () => {
            const r = await api.createEntry({
              client_id: snapshot.client_id,
              entry_date: snapshot.entry_date,
              description: snapshot.description,
              category_id: snapshot.category_id,
              duration_minutes: snapshot.duration_minutes,
              start_time: snapshot.start_time,
              end_time: snapshot.end_time,
            })
            setEntries((es) => [...es, r])
          },
        })
      }
    } catch (e) {
      if (snapshot) setEntries((es) => [...es, snapshot])
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  const blocks = useMemo(() => layoutBlocks(entries, defaultStart), [entries, defaultStart])
  const height = (viewEnd - viewStart) * PX_PER_MIN

  // hour/interval gridlines
  const ticks: number[] = []
  const firstTick = Math.ceil(viewStart / interval) * interval
  for (let t = firstTick; t <= viewEnd; t += interval) ticks.push(t)

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
      {/* Calendar */}
      <div>
        <div className="flex justify-center">
          <button className="text-slate-400 hover:text-slate-700" onClick={() => setViewStart((v) => Math.max(0, v - 180))} aria-label="Show earlier">
            ▲ earlier
          </button>
        </div>
        <div className="mt-1 flex rounded-lg border border-slate-200 bg-white">
          {/* time gutter */}
          <div className="relative w-14 shrink-0 border-r border-slate-100" style={{ height }}>
            {ticks.filter((t) => t % 60 === 0).map((t) => (
              <div key={t} className="absolute right-1 -translate-y-1/2 text-[11px] tabular text-slate-400" style={{ top: (t - viewStart) * PX_PER_MIN }}>
                {minToLabel(t)}
              </div>
            ))}
          </div>
          {/* day column */}
          <div
            ref={colRef}
            className="relative flex-1 cursor-crosshair select-none"
            style={{ height }}
            onMouseDown={onColMouseDown}
          >
            {ticks.map((t) => (
              <div
                key={t}
                className={'pointer-events-none absolute inset-x-0 border-t ' + (t % 60 === 0 ? 'border-slate-200' : 'border-slate-100')}
                style={{ top: (t - viewStart) * PX_PER_MIN }}
              />
            ))}

            {/* drag preview */}
            {drag && (
              <div
                className="pointer-events-none absolute inset-x-1 rounded border border-[var(--accent)] bg-[var(--accent)]/10"
                style={{
                  top: (Math.min(drag.a, drag.b) - viewStart) * PX_PER_MIN,
                  height: Math.max(interval, Math.abs(drag.b - drag.a)) * PX_PER_MIN,
                }}
              />
            )}

            {/* blocks */}
            {blocks.map((b) => {
              const cl = b.entry.client_id != null ? clientById.get(b.entry.client_id) : undefined
              const accent = cl?.accent_color ?? '#3b6ea5'
              const selected = sel?.kind === 'entry' && sel.id === b.entry.id
              const cat = b.entry.category_id != null ? allCats.find((c) => c.id === b.entry.category_id) : undefined
              const moving = moveDrag?.id === b.entry.id
              const dur = b.endMin - b.startMin
              const effStart = moving ? moveDrag!.startMin : b.startMin
              const effEnd = effStart + dur
              return (
                <button
                  key={b.entry.id}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    const origStart = b.startMin
                    const anchorY = e.clientY
                    let moved = false
                    const move = (ev: MouseEvent) => {
                      const dy = ev.clientY - anchorY
                      if (Math.abs(dy) > 3) moved = true
                      if (moved) {
                        const ns = Math.max(0, Math.min(1440 - dur, snap(origStart + dy / PX_PER_MIN)))
                        setMoveDrag({ id: b.entry.id, startMin: ns, dur })
                      }
                    }
                    const up = (ev: MouseEvent) => {
                      window.removeEventListener('mousemove', move)
                      window.removeEventListener('mouseup', up)
                      if (moved) {
                        const ns = Math.max(0, Math.min(1440 - dur, snap(origStart + (ev.clientY - anchorY) / PX_PER_MIN)))
                        patchEntry(b.entry.id, { start_time: minToHHMM(ns), end_time: minToHHMM(ns + dur), duration_minutes: dur })
                      }
                      setMoveDrag(null)
                      setSel({ kind: 'entry', id: b.entry.id })
                    }
                    window.addEventListener('mousemove', move)
                    window.addEventListener('mouseup', up)
                  }}
                  className={
                    'absolute cursor-move select-none overflow-hidden rounded px-1.5 py-0.5 text-left text-[11px] leading-tight ' +
                    (selected || moving ? 'z-10 ring-2 ring-slate-800 ' : '') +
                    (b.placed ? 'border' : 'border border-dashed')
                  }
                  title={b.placed ? undefined : 'Timeless entry — drag to place it at a time'}
                  style={{
                    top: (effStart - viewStart) * PX_PER_MIN + 1,
                    height: dur * PX_PER_MIN - 2,
                    left: `calc(${(b.col * 100) / b.cols}% + 2px)`,
                    width: `calc(${100 / b.cols}% - 4px)`,
                    backgroundColor: withAlpha(accent, '22'),
                    borderColor: accent,
                    color: '#1f2933',
                  }}
                >
                  <div className="font-semibold" style={{ color: accent }}>
                    {cl?.simple_name ?? 'No client'}
                  </div>
                  <div className="truncate text-slate-600">{b.entry.description || cat?.name || ''}</div>
                  <div className="tabular text-slate-400">
                    {minToLabel(effStart)}–{minToLabel(effEnd)}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
        <div className="flex justify-center">
          <button className="text-slate-400 hover:text-slate-700" onClick={() => setViewEnd((v) => Math.min(1440, v + 180))} aria-label="Show later">
            ▼ later
          </button>
        </div>
        <p className="mt-1 text-center text-xs text-slate-400">Click and drag on the calendar to add a block.</p>
      </div>

      {/* Sidebar form */}
      <aside>
        <SidePanel
          sel={sel}
          entries={entries}
          clients={clients}
          allCats={allCats}
          todos={todos}
          onDraftClient={(clientId) => sel?.kind === 'draft' && createFromDraft(sel, clientId)}
          onDraftChange={(patch) => setSel((s) => (s?.kind === 'draft' ? { ...s, ...patch } : s))}
          onPatch={patchEntry}
          onDelete={deleteEntry}
          onClose={() => setSel(null)}
        />
      </aside>
    </div>
  )
}

function SidePanel({
  sel,
  entries,
  clients,
  allCats,
  todos,
  onDraftClient,
  onDraftChange,
  onPatch,
  onDelete,
  onClose,
}: {
  sel: Selection
  entries: Entry[]
  clients: Client[]
  allCats: Category[]
  todos: Todo[]
  onDraftClient: (clientId: number) => void
  onDraftChange: (patch: Partial<Extract<Selection, { kind: 'draft' }>>) => void
  onPatch: (id: number, patch: Partial<Entry>) => void
  onDelete: (id: number) => void
  onClose: () => void
}) {
  // Nothing selected → show the day's widgets (hours-by-client / to-dos).
  if (!sel) {
    return <WidgetPanel entries={entries} clients={clients} allCats={allCats} todos={todos} />
  }

  if (sel.kind === 'draft') {
    const cats = sel.client_id != null ? allCats.filter((c) => c.client_id === sel.client_id) : []
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">New block</h3>
          <button className="text-slate-400 hover:text-slate-700" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="tabular mb-3 text-sm text-slate-500">
          {minToLabel(sel.startMin)} – {minToLabel(sel.endMin)} ({formatHours(sel.endMin - sel.startMin)}h)
        </p>
        <Label text="Client (pick to save)">
          <ClientComboWrap value={sel.client_id} clients={clients} onCommit={(id) => id != null && onDraftClient(id)} />
        </Label>
        <Label text="Description">
          <input
            className={fieldCls}
            value={sel.description}
            onChange={(e) => onDraftChange({ description: e.target.value })}
          />
        </Label>
        <Label text="Category">
          <CategoryComboWrap value={sel.category_id} categories={cats} disabled={sel.client_id == null} onCommit={(id) => onDraftChange({ category_id: id })} />
        </Label>
        <p className="mt-2 text-xs text-slate-400">Choose a client to save this block.</p>
      </div>
    )
  }

  const entry = entries.find((e) => e.id === sel.id)
  if (!entry) return null
  const startMin = entry.start_time ? hhmmToMin(entry.start_time) : 0
  const endMin = entry.end_time ? hhmmToMin(entry.end_time) : startMin
  const cats = entry.client_id != null ? allCats.filter((c) => c.client_id === entry.client_id) : []

  const setStart = (hhmm: string) => {
    const s = hhmmToMin(hhmm)
    const dur = endMin - startMin
    onPatch(entry.id, { start_time: minToHHMM(s), end_time: minToHHMM(s + dur) })
  }
  const setDuration = (mins: number) => {
    onPatch(entry.id, { duration_minutes: mins, end_time: minToHHMM(startMin + mins) })
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Block details</h3>
        <button className="text-slate-400 hover:text-slate-700" onClick={onClose}>
          ✕
        </button>
      </div>
      <Label text="Client">
        <ClientComboWrap
          value={entry.client_id}
          clients={clients}
          onCommit={(id) => id != null && onPatch(entry.id, { client_id: id, category_id: null })}
        />
      </Label>
      <Label text="Description">
        <input className={fieldCls} value={entry.description} onChange={(e) => onPatch(entry.id, { description: e.target.value })} />
      </Label>
      <Label text="Category">
        <CategoryComboWrap value={entry.category_id} categories={cats} disabled={entry.client_id == null} onCommit={(id) => onPatch(entry.id, { category_id: id })} />
      </Label>
      <div className="grid grid-cols-2 gap-3">
        <Label text="Start">
          <input type="time" className={fieldCls + ' tabular'} value={entry.start_time ?? ''} onChange={(e) => setStart(e.target.value)} />
        </Label>
        <Label text="Duration (h)">
          <DurationInput minutes={endMin - startMin} onCommit={setDuration} />
        </Label>
      </div>
      <div className="mt-3 flex justify-between">
        <button className="text-sm text-red-600 hover:underline" onClick={() => onDelete(entry.id)}>
          Delete block
        </button>
        <button className="text-sm text-slate-500 hover:underline" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  )
}

// Default side panel (no block selected): toggle between hours-by-client for
// the day and the top to-dos — mirrors the dashboard widgets.
function WidgetPanel({ entries, clients, allCats, todos }: { entries: EntryRow[]; clients: Client[]; allCats: Category[]; todos: Todo[] }) {
  const [tab, setTab] = useState<'hours' | 'todos'>('hours')
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  const rollup = useMemo(() => {
    const map = new Map<number, { minutes: number; cents: number }>()
    for (const e of entries) {
      if (e.client_id == null) continue
      const g = map.get(e.client_id) ?? { minutes: 0, cents: 0 }
      g.minutes += e.duration_minutes
      const cl = clientById.get(e.client_id)
      const cat = e.category_id != null ? allCats.find((c) => c.id === e.category_id) : undefined
      const rate = cat?.rate_cents != null ? cat.rate_cents : cl?.default_rate_cents ?? 0
      g.cents += Math.round((e.duration_minutes * rate) / 60)
      map.set(e.client_id, g)
    }
    const items = [...map.entries()]
      .map(([cid, g]) => ({ cid, name: clientById.get(cid)?.simple_name ?? 'Unknown', accent: clientById.get(cid)?.accent_color ?? '#3b6ea5', ...g }))
      .sort((a, b) => b.minutes - a.minutes)
    return { items, totalMin: items.reduce((s, i) => s + i.minutes, 0), totalCents: items.reduce((s, i) => s + i.cents, 0) }
  }, [entries, clientById, allCats])

  const topTodos = todos.slice(0, 6)

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex border-b border-slate-200 text-sm">
        <button
          className={'flex-1 px-3 py-2 font-medium ' + (tab === 'hours' ? 'border-b-2 border-slate-800 text-slate-900' : 'text-slate-500 hover:text-slate-800')}
          onClick={() => setTab('hours')}
        >
          Hours by client
        </button>
        <button
          className={'flex-1 px-3 py-2 font-medium ' + (tab === 'todos' ? 'border-b-2 border-slate-800 text-slate-900' : 'text-slate-500 hover:text-slate-800')}
          onClick={() => setTab('todos')}
        >
          To-Dos
        </button>
      </div>

      {tab === 'hours' ? (
        <table className="w-full border-collapse text-sm">
          <tbody>
            {rollup.items.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-slate-400">
                  No entries today.
                </td>
              </tr>
            )}
            {rollup.items.map((it) => (
              <tr key={it.cid} className="border-b border-slate-100">
                <td className="px-3 py-1.5">
                  <button className="flex items-center gap-2 text-left hover:underline" onClick={() => navigate(`#/client/${it.cid}`)} title="Open client">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: it.accent }} />
                    <span className="font-medium text-slate-800">{it.name}</span>
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right tabular text-slate-700">{formatHours2(it.minutes)}</td>
                <td className="px-3 py-1.5 text-right tabular text-slate-800">{formatCents(it.cents)}</td>
              </tr>
            ))}
          </tbody>
          {rollup.items.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-300 font-semibold">
                <td className="px-3 py-2 text-slate-900">Total</td>
                <td className="px-3 py-2 text-right tabular text-slate-900">{formatHours2(rollup.totalMin)}</td>
                <td className="px-3 py-2 text-right tabular text-slate-900">{formatCents(rollup.totalCents)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      ) : (
        <div>
          {topTodos.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-slate-400">No open to-dos.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-100">
              {topTodos.map((t) => {
                const accent = t.client_id != null ? clientById.get(t.client_id)?.accent_color ?? '#94a3b8' : '#cbd5e1'
                return (
                  <li key={t.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                    <span className="truncate text-slate-700">{t.task || '(untitled task)'}</span>
                  </li>
                )
              })}
            </ul>
          )}
          <div className="border-t border-slate-100 px-3 py-2 text-right">
            <button className="text-xs text-[var(--accent)] hover:underline" onClick={() => navigate('#/todos')}>
              View all
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── List view ───────────────────────────────────────────────────────────────
// A spreadsheet-style table of the selected day's entries. Same rules as the
// client grid: every cell edits in place, the last row is always blank, and
// typing in it creates a record (once a client is chosen — entries require one).

interface ListRow extends EntryRow {
  _key: number
}

const LIST_DEFS: ColumnDef[] = [
  { key: 'client', label: 'Client', width: 170 },
  { key: 'description', label: 'Description', width: 300 },
  { key: 'category', label: 'Category', width: 180 },
  { key: 'start_time', label: 'Start', width: 90 },
  { key: 'duration', label: 'Hours', width: 90 },
  { key: 'actions', label: '', width: 40, fixed: true },
]
const LIST_NAV = ['client', 'description', 'category', 'start_time', 'duration']
const LIST_SAVE_FIELDS = ['client_id', 'description', 'category_id', 'duration_minutes', 'start_time', 'end_time'] as const

// 'H:MM' / 'HHMM' / 'HH:MM' -> 'HH:MM'; anything else is left alone.
function normalizeTime(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  const m = /^(\d{1,2}):?(\d{2})$/.exec(s)
  if (!m) return s
  const h = Math.min(23, Number(m[1]))
  const mm = Math.min(59, Number(m[2]))
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

function DayList({ day, clients, allCats, todos }: { day: string; clients: Client[]; allCats: Category[]; todos: Todo[] }) {
  const [rows, setRows] = useState<ListRow[]>([])
  const [loading, setLoading] = useState(true)
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const keyCounter = useRef(1)
  const nextKey = () => keyCounter.current++

  const makeBlank = useCallback(
    (): ListRow => ({
      _key: nextKey(),
      id: 0,
      client_id: null,
      entry_date: day,
      description: '',
      category_id: null,
      duration_minutes: 0,
      start_time: null,
      end_time: null,
      invoice_id: null,
      created_at: '',
      updated_at: '',
    }),
    [day],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const es = await api.listEntriesByDate(day)
        if (cancelled) return
        const sorted = [...es].sort((a, b) => (a.start_time ?? '99:99').localeCompare(b.start_time ?? '99:99') || a.id - b.id)
        setRows([...sorted.map((e) => ({ ...e, _key: nextKey() })), makeBlank()])
      } catch (e) {
        if (!cancelled) pushError(`Couldn't load day: ${(e as Error).message}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [day, makeBlank])

  const { scheduleSave, flushAll, savingCount, hasError } = useAutosave({})

  const colsApi = useColumns('today-list', LIST_DEFS)
  const navKeys = LIST_NAV.filter((k) => colsApi.isVisible(k))
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  const setLocal = useCallback((key: number, patch: Partial<ListRow>) => {
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)))
  }, [])

  const creating = useRef<Set<number>>(new Set())
  const createRow = useCallback(
    async (key: number, seed: ListRow) => {
      const clientId = seed.client_id
      if (clientId == null || creating.current.has(key)) return
      creating.current.add(key)
      try {
        const created = await api.createEntry({
          client_id: clientId,
          entry_date: day,
          description: seed.description,
          category_id: seed.category_id,
          duration_minutes: seed.duration_minutes,
          start_time: seed.start_time,
          end_time: seed.end_time,
        })
        setRows((rs) => {
          const mapped = rs.map((r) => (r._key === key ? { ...r, id: created.id } : r))
          if (!mapped.some((r) => r.id === 0)) mapped.push(makeBlank())
          return mapped
        })
      } catch (e) {
        pushError(`Could not save new entry: ${(e as Error).message}`)
      } finally {
        creating.current.delete(key)
      }
    },
    [day, makeBlank],
  )

  const commit = useCallback(
    (row: ListRow, rawPatch: Partial<ListRow>) => {
      if (row.invoice_id != null) return
      const cur = rowsRef.current.find((r) => r._key === row._key) ?? row
      let patch = rawPatch
      // Keep end_time consistent so timed entries land on the calendar view.
      if ('start_time' in patch || 'duration_minutes' in patch) {
        const m = { ...cur, ...patch }
        patch = {
          ...patch,
          end_time: m.start_time && m.duration_minutes > 0 ? minToHHMM(hhmmToMin(m.start_time) + m.duration_minutes) : null,
        }
      }
      setLocal(row._key, patch)
      const merged = { ...cur, ...patch } as ListRow
      if (merged.id === 0) {
        void createRow(row._key, merged)
        return
      }
      const savePatch: Partial<Entry> = {}
      for (const f of LIST_SAVE_FIELDS) if (f in patch) (savePatch as any)[f] = (patch as any)[f]
      if (Object.keys(savePatch).length) scheduleSave(merged.id, savePatch)
    },
    [createRow, scheduleSave, setLocal],
  )

  async function deleteRow(row: ListRow) {
    if (row.id === 0) return
    if (row.invoice_id != null) {
      pushError('This entry is on an issued invoice and cannot be deleted.')
      return
    }
    const snapshot = { ...row }
    const insertAt = rowsRef.current.findIndex((r) => r._key === row._key)
    setRows((rs) => rs.filter((r) => r._key !== row._key))
    try {
      await api.deleteEntry(row.id)
      pushToast('Entry deleted', {
        actionLabel: 'Undo',
        onAction: async () => {
          if (snapshot.client_id == null) return
          try {
            const r = await api.createEntry({
              client_id: snapshot.client_id,
              entry_date: snapshot.entry_date,
              description: snapshot.description,
              category_id: snapshot.category_id,
              duration_minutes: snapshot.duration_minutes,
              start_time: snapshot.start_time,
              end_time: snapshot.end_time,
            })
            setRows((rs) => {
              const copy = [...rs]
              const blankAt = copy.findIndex((x) => x.id === 0)
              copy.splice(blankAt < 0 ? copy.length : blankAt, 0, { ...r, _key: nextKey() })
              return copy
            })
          } catch (e) {
            pushError(`Undo failed: ${(e as Error).message}`)
          }
        },
      })
    } catch (e) {
      setRows((rs) => {
        const copy = [...rs]
        copy.splice(Math.max(insertAt, 0), 0, snapshot)
        return copy
      })
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  // ── keyboard navigation (key-based so hidden columns are skipped) ──
  const refs = useRef<Map<string, HTMLElement>>(new Map())
  const refKey = (rowKey: number, colKey: string) => `${rowKey}|${colKey}`
  const register = (rowKey: number, colKey: string) => (el: HTMLElement | null) => {
    const k = refKey(rowKey, colKey)
    if (el) refs.current.set(k, el)
    else refs.current.delete(k)
  }
  const focusKey = useCallback((rowIdx: number, colKey: string, caret?: 'start' | 'end') => {
    const rs = rowsRef.current
    if (rowIdx < 0 || rowIdx >= rs.length || !colKey) return
    const el = refs.current.get(refKey(rs[rowIdx]._key, colKey))
    if (!el) return
    el.focus()
    if (el instanceof HTMLInputElement && caret) {
      const n = caret === 'end' ? el.value.length : 0
      try {
        el.setSelectionRange(n, n)
      } catch {
        /* ignore */
      }
    }
  }, [])

  const navKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIdx: number, colKey: string) => {
      const el = e.currentTarget as HTMLElement
      const isText = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      const idx = navKeys.indexOf(colKey)
      const mod = e.ctrlKey || e.metaKey
      if (mod && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault()
        void deleteRow(rowsRef.current[rowIdx])
        return
      }
      const atStart = !isText || ((el as HTMLInputElement).selectionStart === 0 && (el as HTMLInputElement).selectionEnd === 0)
      const atEnd =
        !isText ||
        ((el as HTMLInputElement).selectionStart === (el as HTMLInputElement).value.length &&
          (el as HTMLInputElement).selectionEnd === (el as HTMLInputElement).value.length)
      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          flushAll()
          focusKey(e.shiftKey ? rowIdx - 1 : rowIdx + 1, colKey, 'end')
          break
        case 'Tab':
          e.preventDefault()
          if (e.shiftKey) {
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
            else focusKey(rowIdx - 1, navKeys[navKeys.length - 1], 'end')
          } else {
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'end')
            else focusKey(rowIdx + 1, navKeys[0], 'end')
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          focusKey(rowIdx + 1, colKey, 'end')
          break
        case 'ArrowUp':
          e.preventDefault()
          focusKey(rowIdx - 1, colKey, 'end')
          break
        case 'ArrowLeft':
          if (atStart) {
            e.preventDefault()
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
          }
          break
        case 'ArrowRight':
          if (atEnd) {
            e.preventDefault()
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'start')
          }
          break
      }
    },
    [navKeys, flushAll, focusKey],
  )

  function renderCell(colKey: string, row: ListRow, rowIdx: number) {
    const readOnly = row.invoice_id != null
    const isBlank = row.id === 0
    const onKey = (e: React.KeyboardEvent) => navKeyDown(e, rowIdx, colKey)
    switch (colKey) {
      case 'client': {
        const accent = row.client_id != null ? clientById.get(row.client_id)?.accent_color ?? '#94a3b8' : '#cbd5e1'
        return (
          <td key="client" className="border-r border-slate-100 align-middle" style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
            <ClientCombo
              value={row.client_id}
              clients={clients}
              disabled={readOnly}
              onCommit={(id) => commit(row, { client_id: id, category_id: null })}
              gridKeyDown={onKey}
              registerRef={register(row._key, 'client')}
            />
          </td>
        )
      }
      case 'description':
        return (
          <td key="description" className="border-r border-slate-100 align-middle">
            <WrapText
              wrap={colsApi.wrap}
              value={row.description}
              disabled={readOnly}
              placeholder={isBlank ? 'New entry…' : ''}
              onChange={(v) => commit(row, { description: v })}
              onKeyDown={onKey}
              registerRef={register(row._key, 'description')}
            />
          </td>
        )
      case 'category': {
        const cats = row.client_id != null ? allCats.filter((c) => c.client_id === row.client_id) : []
        return (
          <td key="category" className="border-r border-slate-100 align-middle">
            <CategoryCombo
              value={row.category_id}
              categories={cats}
              disabled={readOnly || row.client_id == null}
              onCommit={(id) => commit(row, { category_id: id })}
              gridKeyDown={onKey}
              registerRef={register(row._key, 'category')}
            />
          </td>
        )
      }
      case 'start_time':
        return (
          <td key="start_time" className="border-r border-slate-100 align-middle">
            <TimeCell
              value={row.start_time}
              readOnly={readOnly}
              onCommit={(v) => commit(row, { start_time: v })}
              onKeyDown={onKey}
              registerRef={register(row._key, 'start_time')}
            />
          </td>
        )
      case 'duration':
        return (
          <td key="duration" className="border-r border-slate-100 align-middle">
            <ListDurationCell
              minutes={row.duration_minutes}
              readOnly={readOnly}
              onCommit={(m) => commit(row, { duration_minutes: m })}
              onKeyDown={onKey}
              registerRef={register(row._key, 'duration')}
            />
          </td>
        )
      case 'actions':
        return (
          <td key="actions" className="px-1 py-1 text-center">
            {!isBlank && !readOnly && (
              <button tabIndex={-1} className="text-slate-300 hover:text-red-500" title="Delete entry" aria-label="Delete entry" onClick={() => deleteRow(row)}>
                ✕
              </button>
            )}
          </td>
        )
      default:
        return null
    }
  }

  const realRows = rows.filter((r) => r.id !== 0)
  const totalMin = realRows.reduce((s, r) => s + r.duration_minutes, 0)

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
      <div>
        <div className="mb-1 flex h-6 items-center justify-end gap-2 text-xs">
          {hasError ? (
            <span className="font-medium text-red-600">⚠ Unsaved changes</span>
          ) : savingCount > 0 ? (
            <span className="text-slate-400">Saving…</span>
          ) : (
            <span className="text-slate-300">Saved</span>
          )}
          <ColumnsMenu cols={colsApi} />
        </div>
        {loading ? (
          <p className="py-10 text-slate-400">Loading day…</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="border-collapse text-sm" style={{ tableLayout: 'fixed', width: colsApi.totalWidth }}>
              <ColGroup cols={colsApi} />
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {colsApi.visible.map((d) =>
                    d.key === 'actions' ? (
                      <th key="actions" className="px-2 py-2" />
                    ) : (
                      <th key={d.key} className={'relative px-2 py-2 ' + (d.key === 'duration' ? 'text-right' : '')}>
                        {d.label}
                        <ColResizer cols={colsApi} colKey={d.key} />
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => {
                  const readOnly = row.invoice_id != null
                  return (
                    <tr
                      key={row._key}
                      className={'border-b border-slate-100 align-middle ' + (readOnly ? 'bg-slate-50/60' : 'bg-white hover:bg-slate-50/40')}
                      title={readOnly ? 'On an issued invoice — read-only' : undefined}
                    >
                      {colsApi.visible.map((d) => renderCell(d.key, row, rowIdx))}
                    </tr>
                  )
                })}
              </tbody>
              {realRows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-300 text-sm font-semibold">
                    {colsApi.visible.map((d) =>
                      d.key === 'duration' ? (
                        <td key="duration" className="px-2 py-2 text-right tabular text-slate-900">
                          {formatHours2(totalMin)}
                        </td>
                      ) : d.key === 'client' ? (
                        <td key="client" className="px-2 py-2 text-slate-900">
                          Total
                        </td>
                      ) : (
                        <td key={d.key} />
                      ),
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-slate-400">
          Entries are added to {dayLabel(day)} · pick a client to save a new row · Start is optional · Arrows / Tab / Enter to move · Ctrl+⌫ delete
        </p>
      </div>

      <aside>
        <WidgetPanel entries={realRows} clients={clients} allCats={allCats} todos={todos} />
      </aside>
    </div>
  )
}

function TimeCell({
  value,
  readOnly,
  onCommit,
  onKeyDown,
  registerRef,
}: {
  value: string | null
  readOnly: boolean
  onCommit: (v: string | null) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}) {
  const [text, setText] = useState(value ?? '')
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!editing) setText(value ?? '')
  }, [value, editing])
  function done() {
    setEditing(false)
    const next = normalizeTime(text)
    setText(next ?? '')
    if (next !== (value ?? null)) onCommit(next)
  }
  return (
    <input
      ref={registerRef}
      className="grid-cell w-full bg-transparent px-2 py-1 tabular outline-none disabled:text-slate-400"
      value={text}
      disabled={readOnly}
      placeholder="HH:MM"
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        if (e.key === 'Enter') done()
        onKeyDown(e)
      }}
    />
  )
}

function ListDurationCell({
  minutes,
  readOnly,
  onCommit,
  onKeyDown,
  registerRef,
}: {
  minutes: number
  readOnly: boolean
  onCommit: (minutes: number) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}) {
  const [text, setText] = useState(() => (minutes ? formatHours(minutes) : ''))
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!editing) setText(minutes ? formatHours(minutes) : '')
  }, [minutes, editing])
  function done() {
    setEditing(false)
    const parsed = parseDuration(text)
    if (parsed == null) {
      setText(minutes ? formatHours(minutes) : '')
      return
    }
    if (parsed !== minutes) onCommit(parsed)
    else setText(parsed ? formatHours(parsed) : '')
  }
  return (
    <input
      ref={registerRef}
      className="grid-cell w-full bg-transparent px-2 py-1 text-right tabular outline-none disabled:text-slate-400"
      value={text}
      disabled={readOnly}
      placeholder="0"
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        if (e.key === 'Enter') done()
        onKeyDown(e)
      }}
    />
  )
}

const fieldCls = 'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[var(--accent)]'

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{text}</span>
      {children}
    </label>
  )
}

// Thin wrappers so the combos render nicely inside the side panel (they expect a
// grid keydown + ref registrar; here navigation is irrelevant).
function ClientComboWrap({ value, clients, onCommit }: { value: number | null; clients: Client[]; onCommit: (id: number | null) => void }) {
  return (
    <div className="rounded-md border border-slate-300 px-1">
      <ClientCombo value={value} clients={clients} onCommit={onCommit} gridKeyDown={() => {}} registerRef={() => {}} />
    </div>
  )
}
function CategoryComboWrap({ value, categories, disabled, onCommit }: { value: number | null; categories: Category[]; disabled?: boolean; onCommit: (id: number | null) => void }) {
  return (
    <div className={'rounded-md border border-slate-300 px-1 ' + (disabled ? 'bg-slate-50' : '')}>
      <CategoryCombo value={value} categories={categories} disabled={disabled} onCommit={onCommit} gridKeyDown={() => {}} registerRef={() => {}} />
    </div>
  )
}

function DurationInput({ minutes, onCommit }: { minutes: number; onCommit: (mins: number) => void }) {
  const [text, setText] = useState(() => formatHours(minutes))
  useEffect(() => setText(formatHours(minutes)), [minutes])
  return (
    <input
      className={fieldCls + ' tabular'}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const p = parseDuration(text)
        if (p == null || p === minutes) setText(formatHours(minutes))
        else onCommit(p)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
    />
  )
}
